<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
</script>

<template>
  <footer>
    <div class="dark-orbs" aria-hidden="true">
      <span class="orb orb-a"></span><span class="orb orb-b"></span><span class="orb orb-c"></span>
    </div>
    <div class="container">
      <div class="footer-grid">
        <div class="footer-brand">
          <div class="footer-brand-row">
            <img
              src="/brand/infoscape-logo-white.svg"
              alt="InfoScape"
              class="footer-logo"
              width="40"
              height="37"
            />
            <h3>InfoScape</h3>
          </div>
          <div class="tagline">{{ t('footer.tagline') }}</div>
          <p v-html="t('footer.blurb')" />
        </div>
        <div class="footer-col">
          <h4>{{ t('footer.workspaceTitle') }}</h4>
          <router-link to="/#workspace">{{ t('footer.workspaceLinks.overview') }}</router-link>
          <router-link to="/#benefits">{{ t('footer.workspaceLinks.benefits') }}</router-link>
          <router-link to="/#ws-features">{{ t('footer.workspaceLinks.features') }}</router-link>
          <router-link to="/#ws-preview">{{ t('footer.workspaceLinks.gallery') }}</router-link>
          <router-link to="/#ws-privacy">{{ t('footer.workspaceLinks.privacy') }}</router-link>
        </div>
        <div class="footer-col">
          <h4>{{ t('footer.atlasTitle') }}</h4>
          <router-link to="/#atlas">{{ t('footer.atlasLinks.overview') }}</router-link>
          <router-link to="/#why-atlas">{{ t('footer.atlasLinks.whyAtlas') }}</router-link>
          <router-link to="/#technology">{{ t('footer.atlasLinks.technology') }}</router-link>
          <router-link to="/#architecture">{{ t('footer.atlasLinks.architecture') }}</router-link>
          <router-link to="/#advantage">{{ t('footer.atlasLinks.advantage') }}</router-link>
          <router-link to="/#compare">{{ t('footer.atlasLinks.compare') }}</router-link>
        </div>
        <div class="footer-col">
          <h4>{{ t('footer.discoverTitle') }}</h4>
          <router-link to="/#sparks">{{ t('footer.discoverLinks.sparks') }}</router-link>
          <router-link to="/#consulting">{{ t('footer.discoverLinks.consulting') }}</router-link>
          <router-link to="/#faq">{{ t('footer.discoverLinks.faq') }}</router-link>
        </div>
        <div class="footer-col footer-contact">
          <h4>{{ t('footer.contactTitle') }}</h4>
          <a href="tel:+491638657037" class="contact-link">
            <svg
              class="contact-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path
                d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"
              />
            </svg>
            <span>+49 163 8657037</span>
          </a>
          <a href="mailto:info@infoscape.ai" class="contact-link">
            <svg
              class="contact-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="m3 7 9 6 9-6" />
            </svg>
            <span>info@infoscape.ai</span>
          </a>
          <a
            href="https://www.terratechholding.com"
            target="_blank"
            rel="noopener"
            class="contact-link"
          >
            <svg
              class="contact-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="M3 12h18" />
              <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
            </svg>
            <span>terratechholding.com</span>
          </a>
          <a
            href="https://www.linkedin.com/company/terratechholding/?viewAsMember=true"
            target="_blank"
            rel="noopener"
            aria-label="LinkedIn – TerraTech Holding"
            class="contact-link"
          >
            <svg
              class="contact-icon"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.36V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0z"
              />
            </svg>
            <span>LinkedIn</span>
          </a>
        </div>
      </div>
      <div class="footer-bottom">
        <span>{{ t('footer.copyright') }}</span>
        <div>
          <router-link to="/imprint" class="footer-legal-link">{{
            t('footer.imprint')
          }}</router-link>
          <router-link to="/privacy" class="footer-legal-link">{{
            t('footer.privacy')
          }}</router-link>
        </div>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.footer-contact .contact-link {
  display: flex;
  align-items: center;
  gap: 10px;
  line-height: 1.4;
  transition: color 0.2s ease;
}

.footer-contact .contact-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: var(--primary-light);
  opacity: 0.85;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.footer-contact .contact-link:hover .contact-icon {
  opacity: 1;
  transform: translateY(-1px);
}

@media (max-width: 720px) {
  .footer-contact .contact-link {
    justify-content: center;
  }
}
</style>
